import { PlanDataWhatToTakeAndWhereToStore } from 'api/client';
import { ReactComponent as BackpackIcon } from 'assets/icons/icon-backpack.svg';
import { ReactComponent as LuggageIcon } from 'assets/icons/icon-luggage.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/icon-trash.svg';
import Button from 'components/button';
import { H4, H5 } from 'components/headings';
import { HideVisually } from 'components/hide-visually';
import TextInput from 'components/text-input';
import produce from 'immer';
import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUnmount } from 'react-use';
import slugify from 'slugify';
import { NavigatePlan } from 'types/base';
import { navigateWithDirection } from 'utils/helpers';
import FormControls from '../form-controls';
import {
  Description,
  IntroCopy,
  QuestionWrapper,
  TextFieldWrapper,
  Title,
} from '../question.styles';
import {
  InputWrapper,
  Item,
  ItemForm,
  ItemList,
  ItemText,
  ListWrapper,
  SpanWrapper,
  StandaloneTextFieldWrapper,
  SubtitleWrapper,
  TitleMultipleStyles,
} from './what-to-take.styles';
interface Props {
  whatToTake?: PlanDataWhatToTakeAndWhereToStore;
  onSetWhatToTake: (whatToTake: PlanDataWhatToTakeAndWhereToStore) => void;
  navigate?: NavigatePlan;
}

const WhatToTake: FC<Props> = (props) => {
  const [newItemValue, setNewItemValue] = useState('');
  const [items, setItems] = useState<string[]>(props.whatToTake?.items ?? []);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<PlanDataWhatToTakeAndWhereToStore>({
    defaultValues: {
      items,
      kitLocation: props.whatToTake?.kitLocation ?? '',
      acknowledged: false,
    },
  });
  // Manually registering the `items` field
  register({ name: 'items' });
  register({ name: 'acknowledged' });

  const onResponseSubmit = handleSubmit((whatToTake, event) => {
    whatToTake.acknowledged = true;
    props.onSetWhatToTake(whatToTake);
    navigateWithDirection(props.navigate, event);
  });

  const addItem = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setItems(
        produce(items, (draft) => {
          draft.push(newItemValue.trim());
        })
      );
      // Reset the input
      setNewItemValue('');
    },
    [newItemValue, items]
  );

  const removeItem = useCallback(
    (index: number) => {
      setItems(
        produce(items, (draft) => {
          draft.splice(index, 1);
        })
      );
    },
    [items]
  );

  useEffect(() => {
    setValue('items', items, { shouldDirty: true });
    setValue('acknowledged', true, { shouldDirty: true });
  }, [items, setValue]);

  const formData = watch();
  useUnmount(() => {
    if (!isSubmitting && isDirty) {
      const whatToTake = { ...formData };
      props.onSetWhatToTake(whatToTake);
    }
  });

  return (
    <>
      <QuestionWrapper>
        <Title>What items will we take when we evacuate?</Title>

        <Description>
          In a bushfire, your house is unsafe and may be damaged or destroyed.
          Your Emergency Evacuation Kit should include the essential items you
          need to keep your family comfortable and safe. Prepare your Emergency
          Evacuation Kit before bushfire season, and keep it in an accessible
          spot. Make sure everyone knows where it is kept.
        </Description>

        <TextFieldWrapper>
          <IntroCopy>
            <H4 as='h2'>Our Emergency Evacuation Kit List</H4>
            <SubtitleWrapper>
              <LuggageIcon width={32} />
              <H5 as='h3'>To prepare before bushfire season:</H5>
            </SubtitleWrapper>
            <ListWrapper>
              <ul>
                <li>Important/legal documents</li>
                <li>Precious items (jewellery, medals)</li>
                <li>Clothing for everyone</li>
                <li>First aid kit</li>
                <li>Drinking water, food for the trip</li>
                <li>Personal hygiene items</li>
                <li>Phone, computer, chargers</li>
              </ul>
            </ListWrapper>

            <SubtitleWrapper>
              <BackpackIcon width={32} />
              <H5 as='h3'>Items to grab when leaving</H5>
            </SubtitleWrapper>
            <ListWrapper>
              <ul>
                <li>Wallet</li>
                <li>Medications, life support equipment</li>
                <li>Phone and charger</li>
                <li>House and car keys</li>
                <li>Laptop and charger</li>
                <li>Pets and supplies (food, bowls, cage, leash, etc)</li>
              </ul>
            </ListWrapper>
          </IntroCopy>

          <ItemForm noValidate onSubmit={addItem}>
            <SpanWrapper>
              <TitleMultipleStyles>
                Other items <span className='inner-span'>(Optional)</span>
              </TitleMultipleStyles>
            </SpanWrapper>
            <InputWrapper>
              <TextInput
                label='Item description'
                type='text'
                id='new-item'
                name='item'
                autoComplete='off'
                placeholder='Enter item'
                value={newItemValue}
                onChange={(event) => setNewItemValue(event.target.value)}
              />
              <Button type='submit' disabled={!newItemValue.trim().length}>
                Add
              </Button>
            </InputWrapper>
            {!!items.length && (
              <ItemList>
                {items.map((item, i) => (
                  <li key={slugify(`${item}-${i}`)}>
                    <Item>
                      <ItemText>{item}</ItemText>{' '}
                      <button type='button' onClick={removeItem.bind(null, i)}>
                        <TrashIcon
                          aria-hidden
                          role='presentation'
                          width={20}
                          height={20}
                        />
                        <HideVisually>Remove</HideVisually>
                      </button>
                    </Item>
                  </li>
                ))}
              </ItemList>
            )}
          </ItemForm>
          <StandaloneTextFieldWrapper>
            <TextInput
              label='Where will you store your Emergency Evacuation Kit?'
              type='text'
              id='emergency-kit-location'
              name='kitLocation'
              autoComplete='off'
              placeholder='Enter location'
              ref={register}
            />
          </StandaloneTextFieldWrapper>
        </TextFieldWrapper>
      </QuestionWrapper>

      <form noValidate onSubmit={onResponseSubmit}>
        <FormControls />
      </form>
    </>
  );
};

export default WhatToTake;
